import React, { useState } from 'react'
import { Outlet, RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom'
import { useMount } from 'react-use'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ProtectedRoute, Login, ErrorHandler } from './Pages'
import AuthProvider, { useAuth } from './Provider/AuthProvider'
import { FC } from './Services'
import { Flex, Spinner, Background } from './Components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { cap } from './State'
import { EnvironmentsList } from './Pages/Environments/List'
import { Environment } from './Pages/Environments/index'
import { MapDocument } from './Pages/Documents'
import { List } from './Pages/Forms/List'
import { Test } from './Pages/Test/List'
import { MappedDocumentsList } from './Pages/Documents/List/List'
import { FormWebList } from './Pages/Documents/FormWeb/List'
import { CompileWebForm } from './Pages/Documents/FormWeb/Compile'

export const queryClient = new QueryClient()

const LightTheme = React.lazy(() => import('./Themes/Light/Theme'))
const DarkTheme = React.lazy(() => import('./Themes/Dark/Theme'))

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools
    })
  )
)

const ThemeSelector = ({ children }) => {
  const { themeComponent, themeClassName } = logicLoadTheme()
  return (
    <>
      <React.Suspense fallback={<></>}>
        {themeComponent}
      </React.Suspense>
      <div className={themeClassName}>
        {children}
      </div>
    </>
  )
}

function logicLoadTheme () {
  const [darkMode] = cap.darkMode.useState()
  const { DEFAULT_THEME, SINGLE_THEME } = window._env_
  if (SINGLE_THEME === 'false') {
    return {
      themeComponent: darkMode ? <DarkTheme /> : <LightTheme />,
      themeClassName: `theme-${darkMode ? 'dark' : 'light'}`
    }
    // gestione multipla del tema
  } else {
    // prendo il default con la cartella
    switch (DEFAULT_THEME) {
      case 'light':
        return {
          themeComponent: <LightTheme />,
          themeClassName: 'theme-light'
        }
      case 'dark':
        return {
          themeComponent: <DarkTheme />,
          themeClassName: 'theme-dark'
        }
    }
  }
}

function AppSetup () {
  const { user: storedUser, onLoginJWT } = useAuth()
  const [loading, setLoading] = useState(true)
  const [showDevtools, setShowDevtools] = React.useState(false)

  React.useEffect(() => {
    // @ts-expect-error
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  useMount(async () => {
    if (FC.authenticated && !storedUser?.accessToken) {
      setLoading(false)
      return true
    }
    storedUser?.accessToken && await onLoginJWT(storedUser?.accessToken)
    setLoading(false)
  })

  return (
    <ThemeSelector>
      <Background whiteBackground>
        <ReactQueryDevtools initialIsOpen={false} />
        {showDevtools && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtoolsProduction />
          </React.Suspense>
        )}
        {loading ? <Flex fw fh><Spinner /></Flex> : <Outlet />}
      </Background>
    </ThemeSelector>
  )
}

const router = createBrowserRouter([
  {
    element: <AuthProvider />,
    errorElement: <ErrorHandler />,
    children: [
      {
        element: <AppSetup />,
        children: [
          {
            element: <ProtectedRoute />,
            children: [
              { path: '*', element: <Navigate to='/environments/list/' /> },
              { path: '/test', element: <Test /> },
              { path: '/environments/*', element: <Navigate to='/environments/list' /> },
              { path: '/environments/list/', element: <EnvironmentsList /> },
              { path: '/environments/:environmentId', element: <Environment /> },
              { path: '/environments/:environmentId/mapDocument/new', element: <MapDocument /> },
              { path: '/environments/:environmentId/mapDocument/:mappedDocumentId', element: <MapDocument /> },
              { path: '/environments/:environmentId/mapDocument/list', element: <MappedDocumentsList /> },
              { path: '/environments/:environmentId/webForm/:webFormId', element: <CompileWebForm /> },
              { path: '/environments/:environmentId/webForm/list', element: <FormWebList /> },
              { path: '/environments/:environmentId/forms', element: <List /> }
            ]
          },
          { path: '/login', element: <Login /> }
        ]
      }
    ]
  }
])
function App () {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}
export default App
